import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
    Plus, Trash2, Upload, Package, FileText, Shield, ChevronDown, ChevronUp,
    Scale, Phone, Award, ClipboardCheck, Users, Sparkles,
} from "lucide-react";

const SLOT_LABELS = {
    etiqueta: "Etiqueta",
    ficha_tecnica: "Ficha Técnica",
    msds: "MSDS",
    certificado_libre_venta: "Cert. Libre Venta",
};
const SLOTS = Object.keys(SLOT_LABELS);

const GLOBAL_CATEGORIES = [
    { value: "diploma_salud", label: "Diploma Min. Salud", icon: Shield },
    { value: "diploma_agricultura", label: "Diploma Min. Agricultura", icon: Shield },
    { value: "poliza_rc", label: "Póliza Responsabilidad Civil", icon: Scale },
];

function ProductChemicalForm({ product, onSaved }) {
    const [form, setForm] = useState({
        brand: product.brand || "",
        active_ingredient: product.active_ingredient || "",
        dose: product.dose || "",
        registration: product.registration || "",
        application_method: product.application_method || "",
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/catalog/products/${product.id}/chemical`, form);
            toast.success("Datos químicos guardados");
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const fields = [
        { key: "brand", label: "Marca", ph: "Ej: BASF" },
        { key: "active_ingredient", label: "Ingrediente Activo", ph: "Ej: Alfacipermetrina" },
        { key: "dose", label: "Dosis", ph: "Ej: 30 ml. x Gln." },
        { key: "registration", label: "Registro Único", ph: "Ej: 2013110365" },
        { key: "application_method", label: "Tipo de Aplicación", ph: "Ej: Aspersión" },
    ];

    return (
        <div className="p-3 rounded-lg border bg-white" data-testid={`chemical-form-${product.id}`}>
            <p className="text-xs font-semibold text-[#CC0000] mb-2 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Datos para certificado de transporte
            </p>
            <div className="grid grid-cols-2 gap-2">
                {fields.map((f) => (
                    <div key={f.key}>
                        <Label className="text-[10px] text-muted-foreground">{f.label}</Label>
                        <Input
                            className="h-8 text-xs"
                            value={form[f.key]}
                            placeholder={f.ph}
                            onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                            data-testid={`chemical-${f.key}-${product.id}`}
                        />
                    </div>
                ))}
            </div>
            <Button size="sm" className="mt-2 h-7 text-xs" onClick={save} disabled={saving} data-testid={`chemical-save-${product.id}`}>
                {saving ? "Guardando..." : "Guardar datos químicos"}
            </Button>
        </div>
    );
}

export default function AdminCatalog() {
    const [tab, setTab] = useState("products");
    // Products
    const [products, setProducts] = useState([]);
    const [showNewProduct, setShowNewProduct] = useState(false);
    const [newProductName, setNewProductName] = useState("");
    const [expandedProduct, setExpandedProduct] = useState(null);
    const [uploadingSlot, setUploadingSlot] = useState(null);
    // Global docs
    const [globalDocs, setGlobalDocs] = useState([]);
    const [uploadingGlobal, setUploadingGlobal] = useState(null);
    // Fixed contacts
    const [fixedContacts, setFixedContacts] = useState([]);
    const [showContactForm, setShowContactForm] = useState(false);
    const [contactForm, setContactForm] = useState({ id: null, name: "", role: "", phone: "" });
    // Tech certs
    const [techCerts, setTechCerts] = useState([]);
    const [techs, setTechs] = useState([]);
    const [showNewCert, setShowNewCert] = useState(false);
    const [certForm, setCertForm] = useState({ technician_id: "", title: "", file: null });

    const loadProducts = useCallback(() => {
        api.get("/admin/catalog/products").then((r) => setProducts(r.data));
    }, []);
    const loadGlobalDocs = useCallback(() => {
        api.get("/admin/catalog/global-docs").then((r) => setGlobalDocs(r.data));
    }, []);
    const loadTechCerts = useCallback(() => {
        api.get("/admin/catalog/tech-certs").then((r) => setTechCerts(r.data));
    }, []);
    const loadFixedContacts = useCallback(() => {
        api.get("/contacts/fixed").then((r) => setFixedContacts(r.data || []));
    }, []);

    useEffect(() => {
        loadProducts();
        loadGlobalDocs();
        loadTechCerts();
        loadFixedContacts();
        api.get("/technicians").then((r) => setTechs(r.data || []));
    }, [loadProducts, loadGlobalDocs, loadTechCerts, loadFixedContacts]);

    // ── Fixed contacts ──
    const openContactForm = (c) => {
        setContactForm(c ? { id: c.id, name: c.name, role: c.role, phone: c.phone || "" } : { id: null, name: "", role: "", phone: "" });
        setShowContactForm(true);
    };
    const saveContact = async () => {
        if (!contactForm.name.trim() || !contactForm.role.trim()) { toast.error("Nombre y cargo son obligatorios"); return; }
        try {
            const body = { name: contactForm.name.trim(), role: contactForm.role.trim(), phone: contactForm.phone.trim() };
            if (contactForm.id) await api.patch(`/contacts/fixed/${contactForm.id}`, body);
            else await api.post("/contacts/fixed", { ...body, sort_order: fixedContacts.length });
            toast.success("Contacto guardado");
            setShowContactForm(false);
            loadFixedContacts();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        }
    };
    const deleteContact = async (id) => {
        await api.delete(`/contacts/fixed/${id}`);
        loadFixedContacts();
    };

    // ── Products ──
    const createProduct = async () => {
        if (!newProductName.trim()) return;
        try {
            const fd = new FormData();
            fd.append("product_name", newProductName.trim());
            await api.post("/admin/catalog/products", fd);
            toast.success("Producto creado");
            setNewProductName("");
            setShowNewProduct(false);
            loadProducts();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const deleteProduct = async (pid) => {
        if (!window.confirm("¿Eliminar este producto y todos sus documentos?")) return;
        await api.delete(`/admin/catalog/products/${pid}`);
        toast.success("Producto eliminado");
        loadProducts();
    };

    const uploadProductDoc = async (pid, slot, file) => {
        setUploadingSlot(`${pid}_${slot}`);
        try {
            const fd = new FormData();
            fd.append("slot", slot);
            fd.append("title", `${slot} - ${file.name}`);
            fd.append("file", file);
            await api.post(`/admin/catalog/products/${pid}/docs`, fd);
            toast.success(slot === "ficha_tecnica" ? "Ficha técnica subida — extrayendo markdown con IA..." : "Documento subido");
            loadProducts();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir");
        } finally {
            setUploadingSlot(null);
        }
    };

    const deleteProductDoc = async (pid, slot) => {
        await api.delete(`/admin/catalog/products/${pid}/docs/${slot}`);
        toast.success("Documento eliminado");
        loadProducts();
    };

    const [regenPid, setRegenPid] = useState(null);
    const regenMarkdown = async (pid) => {
        setRegenPid(pid);
        try {
            await api.post(`/admin/catalog/products/${pid}/extract-markdown`);
            toast.success("Ficha procesada con IA ✓");
            loadProducts();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo procesar la ficha");
        } finally {
            setRegenPid(null);
        }
    };

    // ── Global docs ──
    const uploadGlobalDoc = async (category, title, file) => {
        setUploadingGlobal(category);
        try {
            const fd = new FormData();
            fd.append("category", category);
            fd.append("title", title);
            fd.append("file", file);
            await api.post("/admin/catalog/global-docs", fd);
            toast.success("Documento global actualizado");
            loadGlobalDocs();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setUploadingGlobal(null);
        }
    };

    const deleteGlobalDoc = async (docId) => {
        await api.delete(`/admin/catalog/global-docs/${docId}`);
        toast.success("Eliminado");
        loadGlobalDocs();
    };

    // ── Tech certs ──
    const submitCert = async () => {
        if (!certForm.technician_id || !certForm.title || !certForm.file) return toast.error("Completa todos los campos");
        try {
            const fd = new FormData();
            fd.append("technician_id", certForm.technician_id);
            fd.append("title", certForm.title);
            fd.append("file", certForm.file);
            await api.post("/admin/catalog/tech-certs", fd);
            toast.success("Certificado subido");
            setShowNewCert(false);
            setCertForm({ technician_id: "", title: "", file: null });
            loadTechCerts();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const deleteCert = async (cid) => {
        await api.delete(`/admin/catalog/tech-certs/${cid}`);
        toast.success("Eliminado");
        loadTechCerts();
    };

    return (
        <div data-testid="admin-catalog">
            <div className="flex items-center gap-3 mb-4">
                <Package className="w-6 h-6 text-[hsl(var(--trulyn-navy))]" />
                <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                    Catálogo de Documentos
                </h1>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="mb-4">
                    <TabsTrigger value="products" data-testid="catalog-tab-products">
                        <Package className="w-4 h-4 mr-1" /> Productos ({products.length})
                    </TabsTrigger>
                    <TabsTrigger value="global" data-testid="catalog-tab-global">
                        <Scale className="w-4 h-4 mr-1" /> Globales ({globalDocs.length})
                    </TabsTrigger>
                    <TabsTrigger value="certs" data-testid="catalog-tab-certs">
                        <Award className="w-4 h-4 mr-1" /> Certificaciones ({techCerts.length})
                    </TabsTrigger>
                </TabsList>

                {/* ═══ PRODUCTS TAB ═══ */}
                <TabsContent value="products">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-muted-foreground">{products.length} productos en catálogo</p>
                        <Button size="sm" onClick={() => setShowNewProduct(true)} data-testid="catalog-add-product">
                            <Plus className="w-4 h-4 mr-1" /> Nuevo producto
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {products.map((p) => {
                            const isExpanded = expandedProduct === p.id;
                            const docCount = (p.documents || []).length;
                            return (
                                <Card key={p.id} className="overflow-hidden" data-testid={`product-${p.id}`}>
                                    <div
                                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-50"
                                        onClick={() => setExpandedProduct(isExpanded ? null : p.id)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Package className="w-5 h-5 text-violet-600" />
                                            <div>
                                                <p className="font-semibold text-sm">{p.product_name}</p>
                                                <p className="text-[11px] text-muted-foreground">{docCount}/4 documentos · {p.ficha_tecnica_markdown ? "Markdown ✓" : "Sin markdown"}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="text-xs">{docCount}/4</Badge>
                                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </div>
                                    </div>
                                    {isExpanded && (
                                        <div className="border-t px-4 py-3 space-y-3 bg-zinc-50/50">
                                            <ProductChemicalForm product={p} onSaved={loadProducts} />
                                            {(p.documents || []).some((d) => d.slot === "ficha_tecnica") && (
                                                <div className="flex items-center justify-between p-2 rounded-lg border bg-violet-50/60 border-violet-200">
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <Sparkles className="w-4 h-4 text-violet-600" />
                                                        <span className="text-violet-900">
                                                            {p.ficha_tecnica_markdown
                                                                ? "Ficha procesada para la IA (Jarvis/Silvia)"
                                                                : "Ficha aún no procesada para la IA"}
                                                        </span>
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        variant={p.ficha_tecnica_markdown ? "outline" : "default"}
                                                        className="h-7 text-xs gap-1"
                                                        disabled={regenPid === p.id}
                                                        onClick={() => regenMarkdown(p.id)}
                                                        data-testid={`regen-markdown-${p.id}`}
                                                    >
                                                        <Sparkles className="w-3 h-3" />
                                                        {regenPid === p.id ? "Procesando..." : (p.ficha_tecnica_markdown ? "Reprocesar con IA" : "Generar ficha con IA")}
                                                    </Button>
                                                </div>
                                            )}
                                            {SLOTS.map((slot) => {
                                                const doc = (p.documents || []).find((d) => d.slot === slot);
                                                const isUploading = uploadingSlot === `${p.id}_${slot}`;
                                                return (
                                                    <div key={slot} className="flex items-center justify-between p-2 rounded-lg border bg-white">
                                                        <div className="flex items-center gap-2">
                                                            <FileText className="w-4 h-4 text-zinc-400" />
                                                            <div>
                                                                <p className="text-xs font-semibold">{SLOT_LABELS[slot]}</p>
                                                                {doc ? (
                                                                    <p className="text-[10px] text-green-600">Cargado</p>
                                                                ) : (
                                                                    <p className="text-[10px] text-zinc-400">Sin cargar</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {doc && (
                                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteProductDoc(p.id, slot)}>
                                                                    <Trash2 className="w-3 h-3 text-red-500" />
                                                                </Button>
                                                            )}
                                                            <label className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold cursor-pointer transition-colors ${isUploading ? "bg-zinc-100 text-zinc-400" : "bg-violet-50 text-violet-700 hover:bg-violet-100"}`}>
                                                                <Upload className="w-3 h-3" />
                                                                {isUploading ? "Subiendo..." : (doc ? "Reemplazar" : "Subir")}
                                                                <input
                                                                    type="file"
                                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                                    className="hidden"
                                                                    disabled={isUploading}
                                                                    onChange={(e) => {
                                                                        const f = e.target.files?.[0];
                                                                        if (f) uploadProductDoc(p.id, slot, f);
                                                                        e.target.value = "";
                                                                    }}
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <Button variant="ghost" size="sm" className="text-red-600 text-xs" onClick={() => deleteProduct(p.id)}>
                                                <Trash2 className="w-3 h-3 mr-1" /> Eliminar producto
                                            </Button>
                                        </div>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                    <Dialog open={showNewProduct} onOpenChange={setShowNewProduct}>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Nuevo producto</DialogTitle></DialogHeader>
                            <div>
                                <Label>Nombre del producto</Label>
                                <Input value={newProductName} onChange={(e) => setNewProductName(e.target.value)} placeholder="Ej: Endosona" data-testid="new-product-name" />
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowNewProduct(false)}>Cancelar</Button>
                                <Button onClick={createProduct} disabled={!newProductName.trim()} data-testid="new-product-submit">Crear</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </TabsContent>

                {/* ═══ GLOBAL DOCS TAB ═══ */}
                <TabsContent value="global">
                    <p className="text-sm text-muted-foreground mb-3">Estos documentos se distribuyen automáticamente a todos los clientes</p>
                    <div className="space-y-2">
                        {GLOBAL_CATEGORIES.map((cat) => {
                            const doc = globalDocs.find((d) => d.category === cat.value);
                            const Icon = cat.icon;
                            const isUploading = uploadingGlobal === cat.value;
                            return (
                                <Card key={cat.value} className="p-4 flex items-center justify-between" data-testid={`global-${cat.value}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                                            <Icon className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold">{cat.label}</p>
                                            {doc ? (
                                                <p className="text-[10px] text-green-600">{doc.title}</p>
                                            ) : (
                                                <p className="text-[10px] text-zinc-400">No cargado</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {doc && (
                                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteGlobalDoc(doc.id)}>
                                                <Trash2 className="w-3 h-3 text-red-500" />
                                            </Button>
                                        )}
                                        <label className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-semibold cursor-pointer ${isUploading ? "bg-zinc-100 text-zinc-400" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}>
                                            <Upload className="w-3 h-3" />
                                            {isUploading ? "Subiendo..." : (doc ? "Reemplazar" : "Subir")}
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                className="hidden"
                                                disabled={isUploading}
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    if (f) uploadGlobalDoc(cat.value, `${cat.label} — ${f.name}`, f);
                                                    e.target.value = "";
                                                }}
                                            />
                                        </label>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>

                    {/* ── Fixed contacts (Contact Sheet) ── */}
                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <p className="text-sm font-semibold flex items-center gap-1.5"><Phone className="w-4 h-4 text-[#CC0000]" /> Contactos fijos de la Hoja de Contactos</p>
                                <p className="text-[11px] text-muted-foreground">Aparecen en la hoja de contactos de todos los clientes. Los técnicos se agregan automáticamente según los últimos servicios.</p>
                            </div>
                            <Button size="sm" onClick={() => openContactForm(null)} data-testid="add-fixed-contact">
                                <Plus className="w-4 h-4 mr-1" /> Agregar contacto
                            </Button>
                        </div>
                        {fixedContacts.length === 0 ? (
                            <Card className="p-6 text-center text-sm text-muted-foreground">Aún no hay contactos fijos. Agrega el primero.</Card>
                        ) : (
                            <div className="space-y-2">
                                {fixedContacts.map((c) => (
                                    <Card key={c.id} className="p-3 flex items-center justify-between" data-testid={`fixed-contact-${c.id}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                                                <Phone className="w-4 h-4 text-[#CC0000]" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold">{c.name}</p>
                                                <p className="text-[11px] text-muted-foreground">{c.role}{c.phone ? ` · ${c.phone}` : ""}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openContactForm(c)} data-testid={`edit-fixed-contact-${c.id}`}>
                                                <FileText className="w-3.5 h-3.5 text-zinc-500" />
                                            </Button>
                                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteContact(c.id)} data-testid={`delete-fixed-contact-${c.id}`}>
                                                <Trash2 className="w-3 h-3 text-red-500" />
                                            </Button>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>

                    <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
                        <DialogContent>
                            <DialogHeader><DialogTitle>{contactForm.id ? "Editar contacto" : "Nuevo contacto fijo"}</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div><Label>Nombre *</Label><Input value={contactForm.name} onChange={(e) => setContactForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej: Central de Emergencias" data-testid="fixed-contact-name" /></div>
                                <div><Label>Cargo *</Label><Input value={contactForm.role} onChange={(e) => setContactForm((f) => ({ ...f, role: e.target.value }))} placeholder="Ej: Coordinador de Operaciones" data-testid="fixed-contact-role" /></div>
                                <div><Label>Teléfono</Label><Input value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Ej: 2239-7000" data-testid="fixed-contact-phone" /></div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowContactForm(false)}>Cancelar</Button>
                                <Button onClick={saveContact} data-testid="fixed-contact-save">Guardar</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </TabsContent>

                {/* ═══ TECH CERTIFICATIONS TAB ═══ */}
                <TabsContent value="certs">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-muted-foreground">{techCerts.length} certificaciones</p>
                        <Button size="sm" onClick={() => setShowNewCert(true)} data-testid="catalog-add-cert">
                            <Plus className="w-4 h-4 mr-1" /> Nuevo certificado
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {techCerts.map((c) => {
                            const tech = techs.find((t) => t.user_id === c.technician_id || t.id === c.technician_id);
                            return (
                                <Card key={c.id} className="p-3 flex items-center justify-between" data-testid={`cert-${c.id}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                                            <Award className="w-4 h-4 text-amber-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold">{c.title}</p>
                                            <p className="text-[10px] text-muted-foreground">{tech?.full_name || c.technician_id}</p>
                                        </div>
                                    </div>
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteCert(c.id)}>
                                        <Trash2 className="w-3 h-3 text-red-500" />
                                    </Button>
                                </Card>
                            );
                        })}
                    </div>
                    <Dialog open={showNewCert} onOpenChange={setShowNewCert}>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Nuevo certificado de técnico</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <Label>Técnico</Label>
                                    <Select value={certForm.technician_id} onValueChange={(v) => setCertForm((f) => ({ ...f, technician_id: v }))}>
                                        <SelectTrigger data-testid="cert-tech-select"><SelectValue placeholder="Seleccione técnico" /></SelectTrigger>
                                        <SelectContent>
                                            {techs.map((t) => (
                                                <SelectItem key={t.id} value={t.user_id || t.id}>{t.full_name} ({t.code})</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Título del certificado</Label>
                                    <Input value={certForm.title} onChange={(e) => setCertForm((f) => ({ ...f, title: e.target.value }))} placeholder="Ej: Diploma MIP Avanzado" data-testid="cert-title" />
                                </div>
                                <div>
                                    <Label>Archivo</Label>
                                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="block w-full text-sm mt-1" onChange={(e) => setCertForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowNewCert(false)}>Cancelar</Button>
                                <Button onClick={submitCert} data-testid="cert-submit">Subir</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </TabsContent>
            </Tabs>
        </div>
    );
}
